import React, { useState, useEffect } from "react";
import { Color } from "../../env";
import logo from "../../assets/stumbledwhite.png"

interface OverlayCurtainProps {
    children: React.ReactNode;
    message?: string;
    typingSpeed?: number;
}

const OverlayCurtain: React.FC<OverlayCurtainProps> = ({
    children,
    message = "Welcome to your new dashboard",
    typingSpeed = 70,
}) => {
    const [text, setText] = useState<string>("");
    const [doneTyping, setDoneTyping] = useState<boolean>(false);
    const [rotateLogo, setRotateLogo] = useState<boolean>(false);
    const [fadeOut, setFadeOut] = useState<boolean>(false);
    const [openCurtains, setOpenCurtains] = useState<boolean>(false);
    const [removeOverlay, setRemoveOverlay] = useState<boolean>(false);

    // Typewriter effect
    useEffect(() => {
        let i = 0;
        const interval = setInterval(() => {
            setText((prev) => prev + message.charAt(i));
            i++;
            if (i === message.length) {
                clearInterval(interval);
                setTimeout(() => {
                    setDoneTyping(true); // now the logo will appear
                    setTimeout(() => setRotateLogo(true), 500); // rotate logo after appearing
                }, 600);
            }
        }, typingSpeed);
        return () => clearInterval(interval);
    }, [message, typingSpeed]);

    // After logo rotation, fade text + logo
    useEffect(() => {
        if (rotateLogo) {
            const timer = setTimeout(() => setFadeOut(true), 500);
            return () => clearTimeout(timer);
        }
    }, [rotateLogo]);

    // After fade, open curtains
    useEffect(() => {
        if (fadeOut) {
            const timer = setTimeout(() => setOpenCurtains(true), 0);
            return () => clearTimeout(timer);
        }
    }, [fadeOut]);

    // Remove overlay after curtains open
    useEffect(() => {
        if (openCurtains) {
            const timer = setTimeout(() => setRemoveOverlay(true), 1600);
            return () => clearTimeout(timer);
        }
    }, [openCurtains]);


    return (
        <div style={{ position: "relative" }}>
            {/* Underlying content */}
            <div>{children}</div>

            {/* Overlay */}
            {!removeOverlay && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 9999,
                        overflow: "hidden",
                        pointerEvents: "none",
                        background: "transparent",
                    }}
                >
                    {/* Center text */}
                    <span
                        style={{
                            position: "absolute",
                            top: "35%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "#fff",
                            fontSize: "3rem",
                            fontFamily: "monospace",
                            opacity: fadeOut ? 0 : 1,
                            transition: "opacity 1.2s ease",
                            zIndex: 10000,
                            whiteSpace: "nowrap",
                        }}
                    >
                        {text}
                    </span>

                    {/* Logo */}
                    {doneTyping && (
                        <div
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: `translate(-50%, -50%) rotate(${rotateLogo ? "180deg" : "0deg"})`,
                                transition: "transform 0.6s ease-in-out, opacity 0.6s ease",
                                opacity: fadeOut ? 0 : 1,
                                zIndex: 10000,
                            }}
                        >
                            <img src={logo} alt="Logo" style={{ width: 150, height: 150 }} />
                        </div>
                    )}

                    {/* Single sliding overlay */}
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: Color.Primary,
                            transition: "transform 1.5s ease-in-out",
                            transform: openCurtains ? "translateX(-100%)" : "translateX(0)",
                            zIndex: 9998,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default OverlayCurtain;
